import React, { forwardRef } from 'react';
import { Controller } from 'react-hook-form';
import type { TextInputInstance } from 'react-native';

import { theme } from '@garnish/constants';

import {
  NumberField as GarnishNumberField,
  NumberFieldWithBackground as GarnishNumberFieldWithBackground,
} from '../NumberField';
import { VStack } from '../Stack';
import { BodyText } from '../Text';

import type { ControlledProps, FormProps } from './types';

type ComponentProps = React.ComponentProps<typeof GarnishNumberField>;
type OmittedProps = ControlledProps | 'noticePalette';
type InputValue = number | string | undefined;
type ExtraProps = {
  transformValue?: (value: InputValue) => number;
};
type Props = FormProps & Omit<ComponentProps, OmittedProps> & ExtraProps;

export const NumberField = forwardRef<TextInputInstance, Props>(
  ({ name, control, rules, notice, transformValue, ...rest }, ref) => {
    return (
      <Controller
        name={name}
        control={control}
        rules={rules}
        render={({ field: { onChange, onBlur, value }, fieldState: { invalid, error } }) => (
          <GarnishNumberField
            ref={ref}
            value={transformValue ? transformValue(value as InputValue) : value}
            invalid={invalid}
            noticePalette={invalid ? 'caution' : undefined}
            onBlur={onBlur}
            onChangeText={onChange}
            notice={notice ?? error?.message}
            {...rest}
          />
        )}
      />
    );
  },
);

export const NumberFieldWithBackground = forwardRef<TextInputInstance, Props>(
  ({ name, control, rules, notice, onChangeText, label, transformValue, ...rest }, ref) => {
    return (
      <Controller
        name={name}
        control={control}
        rules={rules}
        render={({ field: { onChange, onBlur, value }, fieldState: { invalid, error } }) => {
          const handleTextChange = (currentValue: string) => {
            onChange(currentValue);
            onChangeText?.(currentValue);
          };

          if (!label) {
            return (
              <GarnishNumberFieldWithBackground
                ref={ref}
                value={transformValue ? transformValue(value as InputValue) : value}
                invalid={invalid}
                notice={notice ?? error?.message}
                noticePalette={invalid ? 'caution' : undefined}
                onBlur={onBlur}
                onChangeText={handleTextChange}
                autoCorrect={false}
                autoCapitalize="none"
                {...rest}
              />
            );
          }

          return (
            <VStack gap={theme.spacing['1']}>
              <BodyText sizeMatch={['18']}>{label}</BodyText>

              <GarnishNumberFieldWithBackground
                ref={ref}
                value={value}
                invalid={invalid}
                notice={notice ?? error?.message}
                noticePalette={invalid ? 'caution' : undefined}
                onBlur={onBlur}
                onChangeText={handleTextChange}
                autoCorrect={false}
                autoCapitalize="none"
                {...rest}
              />
            </VStack>
          );
        }}
      />
    );
  },
);
